import React, { useEffect, useMemo, useRef, useState } from 'react'
import NewVersionBanner from './components/NewVersionBanner'
import RefusedChangeBanner from './components/RefusedChangeBanner'
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import './App.css'
import Sidebar, { PRIMARY_NAV_ITEMS } from './components/Sidebar'
import IconButtonDemo from './components/IconButtonDemo'
import KPICardDemo from './components/KPICardDemo'
import FormFieldDemo from './components/FormFieldDemo'
import GradientButtonDemo from './components/GradientButtonDemo'
import GradientButton from './components/GradientButton'
import Inventory from './pages/Inventory'
import CreateAccount from './screens/CreateAccount'
import Start from './screens/Start'
import NewFarm from './screens/NewFarm'
import TypeCulture from './screens/TypeCulture'
import Species from './screens/Species'
import Stage from './screens/Stage'
import ProductionUnit from './screens/ProductionUnit'
import Navegate from './screens/Navegate'
import Datatable from './screens/Datatable'
import Water from './screens/Water'
import WaterLogCapture from './screens/crewMode/WaterLogCapture'
import FeedCapture from './screens/crewMode/FeedCapture'
import MortalityCapture from './screens/crewMode/MortalityCapture'
import WeightSampleCapture from './screens/crewMode/WeightSampleCapture'
import CrewModeHome from './screens/crewMode/CrewModeHome'
import CrewModeShell from './screens/crewMode/CrewModeShell'
import { syncCrewModeFlagsForFarm } from './screens/crewMode/crewModeFlagSync'
import { loadActiveContextSelection } from './utils/activeContextSelection'
import Feed from './screens/Feed'
import Biomass from './screens/Biomass'
import EditFarm from './screens/EditFarm'
import Lots from './screens/Lots'
import Costs from './screens/Costs'
import Tasks from './screens/Tasks'
import Home from './screens/Home'
import Reports from './screens/Reports'
import NewDashboard from './screens/NewDashboard'
import MortalityManagement from './screens/MortalityManagement'
import SFRManagement from './screens/SFRManagement'
import CarryingCapacity from './screens/CarryingCapacity'
import ScreensIndex from './screens/ScreensIndex'
import TokenSignIn from './screens/TokenSignIn'
import ResetPassword from './screens/ResetPassword'
import OwnerConsole from './screens/OwnerConsole'
import CommunityArtifacts from './screens/CommunityArtifacts'
import CommunityArtifactDetail from './screens/CommunityArtifactDetail'
import CommunityProfile from './screens/CommunityProfile'
import CommunityDeliveries from './screens/CommunityDeliveries'
import ImportWorkspace from './screens/ImportWorkspace'
import ImportRecords from './screens/ImportRecords'
import { ensureOfflineReady } from './offline/initOffline'
import { drainEntityOutboxNow } from './offline/outbox/syncEntityOutboxNow'
import { useStoredPrincipal } from './hooks/useStoredPrincipal'
import { getActiveTenantKey, setActiveTenantKey } from './offline/tenant'
import { clearStoredApiToken, type StoredPrincipal } from './utils/apiAuth'
import { exportLogs } from './utils/log'
import { refreshFarmListFromApi } from './utils/farmWizard'
import { purgeOrphanedLocalData } from './offline/purgeOrphanedLocalData'
import { refreshLotsFromApi } from './utils/lotApi'
import { useT, type TranslationKey } from './i18n'

type ShortcutLink = {
  path: string
  labelKey: TranslationKey
}

type QuickActionLink = {
  path: string
  labelKey: TranslationKey
}

const SHOW_HEADER_SHORTCUTS = false

const shortcutLinks: ShortcutLink[] = SHOW_HEADER_SHORTCUTS
  ? [
    { path: '/create-account', labelKey: 'shortcut.createAccount' },
    { path: '/icon-demo', labelKey: 'shortcut.iconDemo' },
    { path: '/kpi-demo', labelKey: 'shortcut.kpiDemo' },
    { path: '/form-demo', labelKey: 'shortcut.formDemo' },
    { path: '/button-demo', labelKey: 'shortcut.buttonDemo' }
  ]
  : []

const quickActionLinks: QuickActionLink[] = [
  // Pre-validation #56/#61: '/sign-in' dropped (this menu only renders while
  // signed in; the header's sign-out control covers switching account); the
  // import entry buttons were removed from the daily Agua/Alimento/Muestreo
  // screens. Findings #199: Importaciones is open to editors (Hoja diaria).
  { path: '/imports', labelKey: 'qa.imports' },
  // Findings #142: Colaboración (community) is outside the validation —
  // its entries are hidden; the routes stay.
  { path: '/owner', labelKey: 'qa.ownerConsole' },
  { path: '/new-farm', labelKey: 'qa.newFarm' },
  { path: '/edit-farm', labelKey: 'qa.editFarm' },
  { path: '/mortality-matrix', labelKey: 'qa.mortalityMatrix' },
  { path: '/sfr', labelKey: 'qa.sfrMatrix' },
  // Findings #151: density and minimum flow tables, next to the matrices.
  { path: '/carrying-capacity', labelKey: 'qa.carryingCapacity' }
]

const capitalize = (value: string): string => value.length > 0
  ? value.slice(0, 1).toUpperCase() + value.slice(1)
  : value

function SignedInApp({ principal }: Readonly<{ principal: StoredPrincipal }>) {
  const t = useT()
  const location = useLocation()
  const navigate = useNavigate()

  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    globalThis.matchMedia?.('(max-width: 900px)')?.matches ?? false
  )

  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false)
  const quickActionsRef = useRef<HTMLDivElement | null>(null)

  const canManageFarms = principal.role === 'editor' || principal.role === 'admin'
  const canAccessOwnerConsole = principal.role === 'admin'
  const canManageMortalityMatrix = principal.role === 'editor' || principal.role === 'admin'
  const canManageSfrMatrix = principal.role === 'editor' || principal.role === 'admin'

  const visibleShortcutLinks = useMemo(() => {
    return shortcutLinks.filter((link) => {
      if (link.path === '/owner') return canAccessOwnerConsole
      // v2.1 finding #50: `/create-account` is the owner-admin onboarding
      // route, not a public sign-up page. Gate it on admin role instead of
      // sign-in state so the contract matches the route guard below.
      if (link.path === '/create-account') return canAccessOwnerConsole
      return true
    })
  }, [canAccessOwnerConsole])

  const visibleQuickActions = useMemo(() => {
    return quickActionLinks.filter((link) => {
      if (link.path === '/owner') return canAccessOwnerConsole
      if (link.path === '/imports') return canManageFarms
      if (link.path === '/new-farm' || link.path === '/edit-farm') return canManageFarms
      if (link.path === '/mortality-matrix') return canManageMortalityMatrix
      if (link.path === '/sfr') return canManageSfrMatrix
      if (link.path === '/carrying-capacity') return canManageFarms
      return true
    })
  }, [canAccessOwnerConsole, canManageFarms, canManageMortalityMatrix, canManageSfrMatrix])

  const teamKey = principal.tenantKey || getActiveTenantKey()

  const actorFallbackLabel = principal.actorId
    ? `${principal.actorId.slice(0, 8)}...`
    : ''
  const identityLabel = principal.accountDisplayName
    || principal.username
    || principal.email
    || actorFallbackLabel
  const userLabel = identityLabel
    ? `${capitalize(principal.role)} - ${identityLabel}`
    : capitalize(principal.role)

  useEffect(() => {
    const query = globalThis.matchMedia?.('(max-width: 900px)')
    if (!query) return
    const handleChange = (event: MediaQueryListEvent) => {
      setIsCompactLayout(event.matches)
    }

    setIsCompactLayout(query.matches)

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange)
      return () => query.removeEventListener('change', handleChange)
    }

    return undefined
  }, [])

  useEffect(() => {
    setIsQuickActionsOpen(false)
  }, [location.pathname])

  const isViewer = principal?.role === 'viewer'
  const isSidebarDailyRoute = useMemo(() => {
    const pathname = location.pathname.toLowerCase()
    return PRIMARY_NAV_ITEMS.some((item) => {
      const normalized = item.path.toLowerCase()
      return pathname === normalized || pathname.startsWith(`${normalized}/`)
    })
  }, [location.pathname])

  const showNewTaskLauncher = isSidebarDailyRoute && !isViewer
  const goToCreateTask = () => navigate('/tasks?focus=create&flashContext=1')

  const isCrewRoute = location.pathname === '/crew' || location.pathname.startsWith('/crew/')
  const isCrewHome = location.pathname === '/crew'

  const handleSignOut = () => {
    clearStoredApiToken()
    setActiveTenantKey(null)
    navigate('/sign-in')
  }

  useEffect(() => {
    if (!isQuickActionsOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (!quickActionsRef.current) return
      if (!quickActionsRef.current.contains(target)) {
        setIsQuickActionsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsQuickActionsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    globalThis.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      globalThis.removeEventListener('keydown', handleKeyDown)
    }
  }, [isQuickActionsOpen])

  useEffect(() => {
    let cancelled = false
    let refreshInFlight: Promise<void> | null = null
    let orphanPurgeDone = false

    const refreshRemoteContext = async (maxEntries: number) => {
      if (cancelled || typeof navigator === 'undefined' || !navigator.onLine) return
      if (refreshInFlight) {
        await refreshInFlight
        return
      }

      refreshInFlight = (async () => {
        await drainEntityOutboxNow({ maxEntries, maxPasses: 5 })
        const activeFarmId = loadActiveContextSelection().farmId
        await Promise.allSettled([
          refreshFarmListFromApi().then(async (farms) => {
            // findings #76: once per session, drop cached rows of units that
            // no longer exist (only after a successful server refresh).
            if (orphanPurgeDone) return
            orphanPurgeDone = true
            const purged = await purgeOrphanedLocalData(farms)
            if (purged.productionRows + purged.logs + purged.storageKeys > 0) {
              console.info('[app] removed cached data of deleted units', purged)
            }
          }),
          refreshLotsFromApi(),
          // Server-Side Flag Enforcement v0 follow-up: keeps Sidebar's
          // Crew Mode nav-visibility cache fresh without a devtools step.
          activeFarmId ? syncCrewModeFlagsForFarm(activeFarmId) : Promise.resolve()
        ])
      })()

      try {
        await refreshInFlight
      } finally {
        refreshInFlight = null
      }
    }

    const boot = async () => {
      try {
        await ensureOfflineReady()
        await refreshRemoteContext(50)
      } catch {
        // best-effort
      }
    }

    const onOnline = () => {
      void refreshRemoteContext(100)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void refreshRemoteContext(50)
    }

    const onFocus = () => {
      void refreshRemoteContext(50)
    }

    void boot()
    globalThis.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    globalThis.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      globalThis.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      globalThis.removeEventListener('focus', onFocus)
    }
  }, [])

  const routes = (
    <Routes key={location.pathname}>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<Home />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/dashboard-new" element={<NewDashboard />} />
      <Route path="/community" element={<CommunityArtifacts />} />
      <Route path="/community/artifacts" element={<CommunityArtifacts />} />
      <Route path="/community/artifacts/:artifactId" element={<CommunityArtifactDetail />} />
      <Route path="/inventory" element={<Inventory />} />
      <Route path="/preview" element={<Navigate to="/inventory" replace />} />
      <Route path="/sign-in" element={<TokenSignIn />} />
      <Route path="/token-access" element={<TokenSignIn />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/community-profile" element={<CommunityProfile />} />
      <Route path="/community-deliveries" element={<CommunityDeliveries />} />
      <Route path="/imports" element={<ImportRecords />} />
      <Route path="/imports/agua" element={<ImportWorkspace />} />
      <Route path="/owner" element={canAccessOwnerConsole ? <OwnerConsole /> : <Navigate to="/home" replace />} />
      <Route path="/create-account" element={canAccessOwnerConsole ? <CreateAccount /> : <Navigate to="/home" replace />} />
      <Route path="/start" element={<Start />} />
      <Route path="/new-farm" element={canManageFarms ? <NewFarm /> : <Navigate to="/home" replace />} />
      <Route path="/type-culture" element={<TypeCulture />} />
      <Route path="/species" element={<Species />} />
      <Route path="/stage" element={<Stage />} />
      <Route path="/production-unit" element={<ProductionUnit />} />
      <Route path="/navegate" element={<Navegate />} />
      <Route path="/database" element={<Datatable />} />
      <Route path="/water" element={<Water />} />
      <Route path="/crew" element={<CrewModeHome />} />
      <Route path="/crew/water" element={<WaterLogCapture />} />
      <Route path="/crew/feed" element={<FeedCapture />} />
      <Route path="/crew/mortality" element={<MortalityCapture />} />
      <Route path="/crew/weight-sample" element={<WeightSampleCapture />} />
      <Route path="/feed" element={<Feed />} />
      <Route path="/biomass" element={<Biomass />} />
      <Route path="/lots" element={<Lots />} />
      <Route path="/costs" element={<Costs />} />
      <Route path="/edit-farm" element={canManageFarms ? <EditFarm /> : <Navigate to="/home" replace />} />
      <Route path="/tasks" element={<Tasks />} />
      <Route path="/todos" element={<Tasks />} />
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/mortality-matrix" element={canManageMortalityMatrix ? <MortalityManagement /> : <Navigate to="/home" replace />} />
      <Route path="/sfr" element={canManageSfrMatrix ? <SFRManagement /> : <Navigate to="/home" replace />} />
      <Route path="/carrying-capacity" element={canManageFarms ? <CarryingCapacity /> : <Navigate to="/home" replace />} />
      <Route path="/screens" element={<ScreensIndex />} />
      <Route path="/icon-demo" element={<IconButtonDemo />} />
      <Route path="/kpi-demo" element={<KPICardDemo />} />
      <Route path="/form-demo" element={<FormFieldDemo />} />
      <Route path="/button-demo" element={<GradientButtonDemo />} />
    </Routes>
  )

  if (isCrewRoute) {
    return (
      <CrewModeShell onSignOut={handleSignOut} onBackToHome={isCrewHome ? undefined : () => navigate('/crew')}>
        {routes}
      </CrewModeShell>
    )
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="app-main">
        <header className="app-header">
          <div className="app-header-inner">
            <div className="app-header-top">
              <div className="app-header-title">
                <h1>{t('app.title')}</h1>
                <p className="app-header-identity">
                  {t('app.user')}: <strong>{userLabel}</strong>
                </p>
              </div>

              <div className="app-header-actions" ref={quickActionsRef}>
                {showNewTaskLauncher && isCompactLayout && (
                  <GradientButton
                    type="button"
                    size="sm"
                    onClick={goToCreateTask}
                    aria-label={t('aria.createNewTask')}
                    style={{ marginRight: 12 }}
                  >
                    {t('app.newTask')}
                  </GradientButton>
                )}
                <button
                  type="button"
                  className="app-header-link app-quickactions-button"
                  onClick={() => setIsQuickActionsOpen((prev) => !prev)}
                  aria-label={t('aria.settings')}
                  aria-haspopup="menu"
                  aria-expanded={isQuickActionsOpen}
                >
                  {t('app.settings')}
                </button>

                {isQuickActionsOpen && (
                  <div className="app-quickactions-menu" role="menu" aria-label={t('aria.settings')}>
                    {/* Findings #147: no "Nombre del equipo" — it only renamed this browser's offline tenant label. */}

                    {visibleQuickActions.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        role="menuitem"
                        className="app-quickactions-item"
                        onClick={() => setIsQuickActionsOpen(false)}
                      >
                        {t(item.labelKey)}
                      </Link>
                    ))}

                    <div className="app-quickactions-divider" aria-hidden="true" />
                      <button
                        type="button"
                        role="menuitem"
                        className="app-quickactions-item"
                        onClick={() => {
                          const tenant = teamKey
                          const blob = exportLogs(tenant)
                          const url = globalThis.URL.createObjectURL(blob)
                          const anchor = document.createElement('a')
                          anchor.href = url
                          anchor.download = `logs-${tenant}-${new Date().toISOString().replaceAll(/[:.]/g, '-')}.json`
                          document.body.appendChild(anchor)
                          anchor.click()
                          anchor.remove()
                          globalThis.URL.revokeObjectURL(url)
                        }}
                      >
                        <span>{t('app.downloadDiagnostics')}</span>
                        <span style={{ display: 'block', fontSize: 11, color: '#6b7280', fontWeight: 400 }}>
                          {t('app.downloadDiagnostics.hint')}
                        </span>
                      </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="app-quickactions-item app-quickactions-danger"
                      onClick={() => {
                        setIsQuickActionsOpen(false)
                        handleSignOut()
                      }}
                    >
                      {t('app.signOut')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {visibleShortcutLinks.length > 0 && (
              <nav className="app-header-links">
                {visibleShortcutLinks.map((link) => (
                  <Link key={link.path} to={link.path} className="app-header-link">
                    {t(link.labelKey)}
                  </Link>
                ))}
              </nav>
            )}
          </div>
          <NewVersionBanner />
        </header>

        <main className="app-content">
          <RefusedChangeBanner variant="office" />
          {routes}
        </main>

        {showNewTaskLauncher && !isCompactLayout && (
          <GradientButton
            type="button"
            size="lg"
            onClick={goToCreateTask}
            aria-label={t('aria.createNewTask')}
            style={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              borderRadius: 999,
              zIndex: 20
            }}
          >
            {t('app.newTask')}
          </GradientButton>
        )}
      </div>
    </div>
  )
}

export default function App() {
  const principal = useStoredPrincipal()
  const location = useLocation()
  const requireTokenAuth = import.meta.env.VITE_REQUIRE_TOKEN_AUTH !== 'false'
  const isPublicAuthRoute = location.pathname === '/sign-in'
    || location.pathname === '/token-access'
    || location.pathname === '/reset-password'

  if (requireTokenAuth && !principal) {
    const to = `${location.pathname}${location.search}`
    if (!isPublicAuthRoute) {
      return <Navigate to={`/sign-in?to=${encodeURIComponent(to)}`} replace />
    }

    if (location.pathname === '/reset-password') {
      return (
        <div style={{ padding: 24, maxWidth: 820, margin: '0 auto' }}>
          <ResetPassword />
        </div>
      )
    }

    return (
      <div style={{ padding: 24, maxWidth: 820, margin: '0 auto' }}>
        <TokenSignIn />
      </div>
    )
  }

  // Field-test mode always expects a resolved principal.
  if (!principal) {
    return null
  }

  return <SignedInApp principal={principal} />
}
